import { Component, Host, h, State, Prop, Element } from '@stencil/core';
import { JSX, Method } from '@stencil/core/internal';

@Component({
  tag: 'lilu-product-tile',
  styleUrl: 'lilu-product-tile.scss',
  shadow: true,
})
export class LiluProductTile {
  @Prop() dataProductId: string;
  @Prop() dataProductUrl = '';
  @Prop() hasTallVariant = false;

  @State() isLoading: boolean;
  @State() brandElement: HTMLElement;
  @State() isHover = false;

  @Element() el: HTMLElement;

  modalContent: HTMLElement;
  modal: HTMLElement;
  hoverDesktopDiv: HTMLDivElement;

  checkMousePosition = (event: MouseEvent) => {
    // function should be declare like that to be able to be deleted as event listener
    const isElementVisible = (elem: HTMLElement) => {
      return !!(elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length);
    };

    if (!isElementVisible(this.el)) {
      return;
    }

    const rect = this.el.getBoundingClientRect();
    const isInDiv = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom + this.hoverDesktopDiv.clientHeight;

    if (!isInDiv) {
      this.handleMouseHover(false);
    }
  };

  handleMouseHover(hover: boolean) {
    if (window.innerWidth >= 1024) {
      this.isHover = hover;

      const tileCarousel: HTMLLiluCarouselElement = this.el.querySelector('lilu-carousel');
      tileCarousel?.setAttribute('show-beauty-shot', hover.toString());
      tileCarousel?.shadowRoot.querySelector('.container').classList.toggle('is-hover', hover);

      const hoverButton: HTMLLiluHoverButtonElement = this.el.querySelector('lilu-hover-button');
      hoverButton?.setAttribute('hovered', hover.toString());

      if (hover) {
        document.querySelector('html').addEventListener('mousemove', this.checkMousePosition);
      } else {
        document.querySelector('html').removeEventListener('mousemove', this.checkMousePosition);
      }
    }
  }

  initProperties() {
    this.brandElement = this.el.querySelector('[slot="brand"]');
    this.modalContent = document.querySelector(`[data-modal="default"] .content`);
    this.modal = document.querySelector(`[data-modal="default"]`);
  }

  componentWillLoad() {
    this.initProperties();
    this.isLoading = !this.dataProductId;
  }

  componentDidLoad() {
    this.hoverDesktopDiv = this.el.shadowRoot.querySelector('.hover-desktop');
  }

  renderSkeleton(): JSX.Element {
    return (
      <div class="product-tile skeleton">
        <div class="image skeleton-loader"></div>
        <div class="price skeleton-loader"></div>
        <div class="title skeleton-loader"></div>
        <div class="colors skeleton-loader"></div>
      </div>
    );
  }

  renderProductTile(): JSX.Element {
    return (
      <div id={`${this.dataProductId}`} class={`product-tile ${this.isHover ? 'open' : ''}`} onMouseEnter={() => this.handleMouseHover(true)}>
        <div class="top">
          <div class={`top-left ${this.isHover ? 'hide' : ''}`}>
            <slot name="top-left"></slot>
          </div>
          <div class="top-right">
            <slot name="top-right"></slot>
          </div>
          <div class="bottom-left">
            <slot name="bottom-left"></slot>
          </div>
          <div class="bottom-right">
            <slot name="bottom-right"></slot>
          </div>
          <div class="img">
            <slot name="img"></slot>
          </div>
        </div>
        <div class="bottom">
          <div class={`sub-header ${this.brandElement ? 'brandElement' : ''}`}>
            <div class="price">
              <slot name="price"></slot>
            </div>
            {this.brandElement ? <slot name="brand"></slot> : ''}
          </div>
          <div class="header">
            <a href={`${this.dataProductUrl}`}>
              <slot name="title"></slot>
            </a>
          </div>
          <div class={`variant ${this.hasTallVariant ? 'tall' : ''}`}>
            <div class="label">
              <slot name="variant-label"></slot>
            </div>
            <div class="color-picker">
              <div>
                <slot name="color-picker"></slot>
              </div>
              <div>
                <slot name="energy-label"></slot>
              </div>
            </div>
          </div>
          <div class="hover-desktop">
            <div class="variant-switcher swiper-no-swiping">
              <slot name="variant-switcher"></slot>
            </div>
            <div class="delivery">
              <slot name="delivery"></slot>
            </div>
            <div class="accessible">
              <slot name="accessible"></slot>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    return (
      <Host data-product-id={this.dataProductId} data-product-url={this.dataProductUrl} class={`${this.isLoading ? 'skeleton-tile' : ''}`}>
        {this.isLoading ? this.renderSkeleton() : this.renderProductTile()}
      </Host>
    );
  }

  hasContent(obj): boolean {
    return obj && Object.keys(obj).length > 0;
  }

  @Method()
  async setJSONData(productJSON) {
    if (!this.isLoading) return;

    // in host
    this.dataProductId = productJSON.code;
    this.dataProductUrl = productJSON.url;

    this.el.removeAttribute('product-to-load');

    let content = '';

    // Elements in slot
    content += `<lilu-wishlist slot="top-right" is-wishlist="${productJSON.inWishlist}" product-id="${productJSON.code}"></lilu-wishlist>`;

    if (productJSON.badgeText) {
      content += `
      <lilu-badge slot="bottom-left">${productJSON.badgeText}%</lilu-badge>
      `;
    }

    content += `
    <div slot="img">
      <lilu-carousel
        class="product-image"
        data-product-url="${this.dataProductUrl}"
        images='${JSON.stringify(productJSON.galleryImages)}'
        show-beauty-shot="false">
      </lilu-carousel>
    </div>`;

    content += `
    <lilu-price
      data-testauto="product-price"
      slot="price"
      type="${productJSON.price.priceType}"
      size="size-md">
        ${productJSON.price.currencyIso}&nbsp;${productJSON.price.value.toFixed(2)}
      </lilu-price>
    `;

    if (this.hasContent(productJSON.originalPrice)) {
      content += `
      <lilu-price
        data-testauto="product-price"
        slot="price"
        type="sale"
        size="size-md">
          ${productJSON.originalPrice.currencyIso}&nbsp;${productJSON.originalPrice.value.toFixed(2)}
        </lilu-price>
      `;
    }
    if (productJSON.brandName) {
      content += `
      <lilu-brand-label slot="brand" link="${productJSON.brandUrl}">${productJSON.brandName}</lilu-brand-label>
      `;
    }

    content += `
    <div slot="title" data-testauto="product-tile-title">${productJSON.title}</div>
    `;

    if (productJSON.isConfigurable) {
      content += `
      <div slot="variant-label">
        <lilu-p font="light" size="size-12 lg-size-14" data-testauto="product-variant-more" class="size-12 lg-size-14" part="size-12 lg-size-14">
          Mehr Varianten
        </lilu-p>
      </div>
      `;
    }

    if (this.hasContent(productJSON.variantColors)) {
      content += `
        <lilu-color-picker slot="color-picker"
          colors='${JSON.stringify(productJSON.variantColors)}'
          multi-select="false"
          size="size-sm"
          limit="5"
          variant-switch="true"
          ajax-url="/${document.documentElement.lang}/variantSwitch/"
          product-id="${this.dataProductId}">
        </lilu-color-picker>
      `;
    }

    if (this.hasContent(productJSON.material)) {
      content += `
        <lilu-p
          slot="variant-switcher"
          font="light"
          size="size-12 lg-size-14"
          appearance="uppercase"
          class="size-12 lg-size-14"
          part="size-12 lg-size-14">
          ${productJSON.material}
        </lilu-p>
      `;
    }

    if (this.hasContent(productJSON.variantMaterials)) {
      content += `
        <lilu-select
          slot="variant-switcher"
          hybris-materials-json=true
          data-testauto="product-variant-size"
          options='${JSON.stringify(productJSON.variantMaterials)}'
          product-id="${this.dataProductId}">
        </lilu-select>
      `;
    } else if (productJSON.measure) {
      content += `
        <lilu-p
          slot="variant-switcher"
          font="light"
          size="size-12 lg-size-14"
          appearance="uppercase"
          class="size-12 lg-size-14"
          part="size-12 lg-size-14">
          ${productJSON.measure}
          </lilu-p>
      `;
    }

    if (this.hasContent(productJSON.delivery)) {
      content += `
        <lilu-p
          slot="delivery"
          icon="${productJSON.delivery.iconValue}"
          icon_color="${productJSON.delivery.iconColor}"
          font="regular"
          size="size-10 lg-size-12"
          data-testauto="product-tile-delivery-time"
          icon_size="size-10 lg-size-12"
          class="size-10 lg-size-12">
          ${productJSON.delivery.deliveryText}
        </lilu-p>
      `;
    }

    content += `
      <lilu-modal-link
        data-modal-show="favorite"
        slot="accessible"
        data-axios-url="/de/standorte/lilu/modal"
        data-testauto="product-tile-pickup-select-fav">
          <input type="hidden" name="productId" value="${this.dataProductId}">
          <input slot="input" type="hidden" name="showReservation" value="false">
          <input slot="input" type="hidden" name="filterResult" value="false">
          <input slot="input" type="hidden" name="pickupType" value="showOthers">
          <input slot="input" type="hidden" name="storeFinderMode" value="favorite">

          <lilu-p
            icon="${productJSON.accessibility.iconValue}"
            icon_color="${productJSON.accessibility.iconColor}"
            font="regular"
            size="size-10 lg-size-12"
            icon_size="size-12 lg-size-14"
            class="size-10 lg-size-12">
              <u>${productJSON.accessibility.accessText}</u>
          </lilu-p>
   </lilu-modal-link>
    `;

    this.el.innerHTML += content;

    this.initProperties();
    this.isLoading = false;
  }
}
